import {
  ProcessSupervisionStepParams,
  InteractionStepType,
  MessageRole,
  CandidateMessageWithMetadata,
  MultiTurnInteractionConfiguration,
  StepParamsForTypeWithStage,
  IndexedInteractionStepResponseForType,
} from '@scale/shared/types/chat';
import { extractPrefilledMessages } from '@scale/shared/chat/response/validation';
import _ from 'lodash';
import { ChatTaskParamsStages } from '@scale/shared/chat/steps';
import { Message } from '@scale/shared/types/chat-completion';
import { unchunk } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/unchunking';
import { DEFAULT_PROCESS_CHUNKING_STRATEGY } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/types';
import { ProcessChunkingStrategy } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/chunking';
import logger from '@scale/frontend-lib/logging';

export const formatPromptWithTemplate = (
  prompt: string,
  template?: string,
  variables?: Record<string, string>,
): string => {
  if (!template) {
    return prompt;
  }
  let filledTemplate = template;
  if (variables) {
    filledTemplate = fillStringWithTemplateVariables(template, variables);
  }
  return filledTemplate.replaceAll('{prompt_input}', prompt);
};

export const fillStringWithTemplateVariables = (
  str: string,
  variables: Record<string, any>,
): string => {
  const templateRegex = /{{([^{}]+)}}|\{([^{}]+)\}/g;
  return str.replace(templateRegex, (match, doubleBracketPath, singleBracketPath) => {
    const path = (doubleBracketPath || singleBracketPath).trim();
    const value = _.get(variables, path);
    return value !== undefined ? String(value) : match;
  });
};
// Helper function to get the first initial message content
const getFirstInitialMessageContent = (config: MultiTurnInteractionConfiguration): string => {
  const taskingUserMessages = config.initialMessages?.filter(
    (message: any) => message.role === MessageRole.User,
  );
  return taskingUserMessages && taskingUserMessages.length > 0
    ? (taskingUserMessages[0].content as string)
    : '';
};

// Helper function to generate the process supervision prompt
const generateProcessSupervisionPrompt = (
  config: MultiTurnInteractionConfiguration,
  allProcessSupervisionResponses: IndexedInteractionStepResponseForType<InteractionStepType.ProcessSupervision>[],
  allExperimentalToolUseResponses: IndexedInteractionStepResponseForType<InteractionStepType.ExperimentalToolUse>[],
  mostRecentPrompt: string,
): string => {
  const getFlattenedCorrectChunks = (responses: any[]): any[] =>
    responses.flatMap((response: any) => response.context.correctChunks || []);

  const processSupervisionPrompt = allProcessSupervisionResponses?.[0]?.context?.prompt;
  const processSupervisionStep = config.turn.find(
    (step: any) => step.type === InteractionStepType.ProcessSupervision,
  ) as ProcessSupervisionStepParams | undefined;

  const chunkingStrategyPrompt =
    processSupervisionStep?.params?.process_chunking_strategy === 'ChunkByStepNumber'
      ? ' Please make sure that the step header and the title of the step header are in bold text.'
      : '';

  if (allExperimentalToolUseResponses.length) {
    const flattenedCorrectChunks =
      allExperimentalToolUseResponses[allExperimentalToolUseResponses.length - 1]?.context
        ?.correctChunksAggregated || [];

    return flattenedCorrectChunks.length
      ? flattenedCorrectChunks.map((chunk: any) => chunk.rewrite || chunk.content).join('\n')
      : mostRecentPrompt;
  }

  const flattenedCorrectChunks = getFlattenedCorrectChunks(allProcessSupervisionResponses);
  const allRawChunkContent = allProcessSupervisionResponses[
    allProcessSupervisionResponses.length - 1
  ]?.context?.chunks
    ?.map((chunk: any) => chunk.content)
    .join('\n');

  if (flattenedCorrectChunks?.length) {
    const lastChunk = flattenedCorrectChunks[flattenedCorrectChunks.length - 1];
    const previousChunks = flattenedCorrectChunks.slice(0, -1);

    // Add previous chunks with their reflections and the most recent chunk without reflection
    const combinedChunks = [
      ...previousChunks.map(
        (chunk: any) =>
          `${chunk.rewrite || chunk.content}${chunk.reflection ? '\n\n' + chunk.reflection : ''}`,
      ),
      `${lastChunk.rewrite || lastChunk.content}`,
    ];
    const { output: combinedChunkTxt } = unchunk(combinedChunks, {
      strategy: (processSupervisionStep?.params?.process_chunking_strategy ||
        DEFAULT_PROCESS_CHUNKING_STRATEGY) as ProcessChunkingStrategy,
      params: processSupervisionStep?.params?.process_chunking_strategy_params || {},
    });

    if (processSupervisionStep?.params?.remove_prompt_templating) {
      return combinedChunkTxt;
    }

    let prompt = `You are solving this question:\n\n"""${processSupervisionPrompt}"""\n\n`;
    prompt += `This is your response so far:\n\n`;

    prompt += combinedChunkTxt;

    prompt += '\n\n';

    // Add special instruction for the most recent reflection
    if (lastChunk.reflection) {
      prompt += `You just thought to yourself this thought, which is a correct and accurate reflection that you must follow going forward: "${lastChunk.reflection}". You must now follow that thought/approach/reflection as you reason through the solution. You absolutely MUST adhere to this thought as you continue to solve the problem. If the thought/reflection says you are going to do something a certain way, YOU MUST do it that way.\n\n`;
    }

    prompt += `Pick up where you left off and complete the answer. Do not repeat anything from what you've already written. Please do not add extra commentary such as "I'm going to continue from where I left off" or anything like that, just pick up and continue towards the solution with your prior thoughts and reflections in mind. ${chunkingStrategyPrompt}`;

    if (processSupervisionStep?.params?.custom_prompt_template) {
      return fillStringWithTemplateVariables(processSupervisionStep.params.custom_prompt_template, {
        baseSupervisionRegenerationPrompt: prompt,
        originalUserPrompt: processSupervisionPrompt,
        allRawChunkContent,
        allCorrectChunkContent: combinedChunkTxt,
        currentCorrectedChunkObject: {
          ...lastChunk,
          ...Object.fromEntries(
            Object.entries(lastChunk).map(([key, value]) => [
              key,
              Array.isArray(value) ? value.toString() : value,
            ]),
          ),
        },
      });
    }
    return prompt;
  }

  return mostRecentPrompt;
};

// Main function to generate the chat prompt
export const generateChatPrompt = (
  step: StepParamsForTypeWithStage<InteractionStepType.ModelResponseSelector>,
  config: MultiTurnInteractionConfiguration,
  allExperimentalToolUseResponses: IndexedInteractionStepResponseForType<InteractionStepType.ExperimentalToolUse>[],
  allProcessSupervisionResponses: IndexedInteractionStepResponseForType<InteractionStepType.ProcessSupervision>[],
  mostRecentPrompt: string,
  chatMessages: Message[],
): string => {
  if (step.params.use_first_initial_message) {
    return getFirstInitialMessageContent(config);
  } else if (allProcessSupervisionResponses.length) {
    return generateProcessSupervisionPrompt(
      config,
      allProcessSupervisionResponses,
      allExperimentalToolUseResponses,
      mostRecentPrompt,
    );
  } else if (step.stage === ChatTaskParamsStages.After) {
    return chatMessages[chatMessages.length - 1]?.content;
  } else {
    return mostRecentPrompt;
  }
};

// todo @dannyadkins add types
export const formatChatHistory = (
  chatHistory: Message[],
  config: {
    systemPrompt?: string;
    alwaysAddSystemPromptAsFirstMessage?: boolean;
    systemPromptGptOnly?: boolean;
    userPromptTemplate?: string;
    templateVariables?: Record<string, string>;
    useFirstInitialMessage?: boolean;
    resetChatHistoryOnEachTurn?: boolean;
    providedChatHistoryJson?: string;
    includeCurrentTurnInChatHistory?: boolean;
    mostRecentModelResponse?: string;
    mostRecentMessageMetadata?: Record<string, any>;
    mostRecentPromptForStepId?: string;
    currentTurnMessages?: Message[]; // Handle cases with 3+ MRS steps per turn, used with includeCurrentTurnInChatHistory
  },
): Message[] => {
  let historyChopped = chatHistory.slice(0, -1);

  if (config.systemPrompt?.length) {
    if (config.alwaysAddSystemPromptAsFirstMessage) {
      historyChopped.unshift({
        role: MessageRole.System,
        content: config.systemPrompt,
      });
    } else {
      historyChopped.push({
        role: MessageRole.System,
        content: config.systemPrompt,
      });
    }
  }

  if (config.userPromptTemplate?.length) {
    historyChopped = historyChopped.map((message: Message) => {
      if (message.role === MessageRole.User) {
        return {
          ...message,
          content: formatPromptWithTemplate(
            message.content,
            config.userPromptTemplate,
            config.templateVariables,
          ),
        };
      }
      return message;
    });
  }

  if (config.useFirstInitialMessage || config.resetChatHistoryOnEachTurn) {
    historyChopped = historyChopped.filter(message => message.role === MessageRole.System);
  }

  if (config.providedChatHistoryJson) {
    try {
      historyChopped.unshift(...(JSON.parse(config.providedChatHistoryJson) as Message[]));
    } catch (e) {
      logger.error('Error parsing provided chat history JSON', {
        error: e,
        json: config.providedChatHistoryJson,
      });
      throw e;
    }
  }

  // Handles passing context between 2+ MRS steps per turn
  if (config.includeCurrentTurnInChatHistory) {
    if (Array.isArray(config.currentTurnMessages) && config.currentTurnMessages.length > 0) {
      historyChopped.push(...config.currentTurnMessages);
    } else if (config.mostRecentModelResponse?.length && config.mostRecentPromptForStepId?.length) {
      // Fallback to original logic
      historyChopped.push(
        {
          role: MessageRole.User,
          content: config.mostRecentPromptForStepId,
        },
        {
          role: MessageRole.Assistant,
          content: config.mostRecentModelResponse,
          messageMetadata: config.mostRecentMessageMetadata,
        },
      );
    }
  }

  return historyChopped;
};

export const unrandomizeText = (rawText: string, candidateOrder: number[]) => {
  const isReversed = candidateOrder[0] === 1;

  if (!isReversed) {
    return rawText;
  }

  const placeholder = '___placeholder___';

  const response2Regex = new RegExp('@Response 2', 'g');
  const response1Regex = new RegExp('@Response 1', 'g');
  const placeholderRegex = new RegExp(placeholder, 'g');

  const response1Replaced = rawText.replace(response1Regex, placeholder);

  const response2Replaced = response1Replaced.replace(response2Regex, 'Response 1');

  const final = response2Replaced.replace(placeholderRegex, 'Response 2');

  return final;
};

export const getInitialLikertScore = (
  candidatesWithId: CandidateMessageWithMetadata[],
  step: { params: { skip_initial_messages?: boolean } },
  config: { initialMessages?: any[] },
  candidateOrder: number[],
  maxLikert: number,
  numberOfTurns: number,
): number | undefined => {
  if (candidatesWithId.length !== 2) {
    return undefined;
  }
  const initialMessages = !step.params.skip_initial_messages ? config.initialMessages ?? [] : [];
  const { taskingAssistantMessages } = extractPrefilledMessages(initialMessages);
  const turnIndex = numberOfTurns - 1;
  let score: number | undefined;
  if (turnIndex < taskingAssistantMessages.length) {
    score = taskingAssistantMessages[turnIndex].initialLikertScore;
    if (!_.isNil(score)) {
      const isReversed = candidateOrder[0] === 1;
      score = isReversed ? maxLikert - score + 1 : score;
    }
  }
  return score;
};
